import { format, parseISO } from "date-fns"

type DepotValidity = {
  valid_from: string
  valid_until?: string | null
}

/**
 * Whether a depot is in use on a `yyyy-MM-dd` date. Both bounds are inclusive; a null
 * `valid_until` means the depot has no end date.
 */
export const isDepotActiveOn = (depot: DepotValidity, date: string): boolean =>
  depot.valid_from <= date && (!depot.valid_until || depot.valid_until >= date)

/**
 * Depots that can be picked for something spanning `startDate`..`endDate`, i.e. active on
 * both ends. `keep` retains a depot that is already chosen, so editing a record that points
 * at a since-closed depot doesn't blank its picker.
 */
export const selectableDepots = <T extends DepotValidity>(
  depots: T[],
  startDate: string,
  endDate: string = startDate,
  keep?: (depot: T) => boolean,
): T[] =>
  depots.filter(
    (depot) =>
      keep?.(depot) || (isDepotActiveOn(depot, startDate) && isDepotActiveOn(depot, endDate)),
  )

/** The `valid_until` date when the depot closed before `date`, otherwise null. */
export const depotClosedOn = (depot: DepotValidity, date: string): string | null =>
  depot.valid_until && depot.valid_until < date ? depot.valid_until : null

/** Suffix for a closed depot's option label, e.g. "(deactivated 30 Jun 2026)". */
export const deactivatedLabel = (closedOn: string): string =>
  `(deactivated ${format(parseISO(closedOn), "d MMM yyyy")})`

/**
 * Every depot paired with the date it closed (null while open), closed ones last so they sit
 * below the usable ones in a picker.
 */
export const depotPickerOptions = <T extends DepotValidity>(
  depots: T[],
  today: string,
): { depot: T; closedOn: string | null }[] =>
  depots
    .map((depot) => ({ depot, closedOn: depotClosedOn(depot, today) }))
    .sort((a, b) => Number(Boolean(a.closedOn)) - Number(Boolean(b.closedOn)))
